import { useState } from "react";
import { Modal, NewFieldLabel, NewFieldStyleOverrides, PhoneField, TextField, ToastMessageProps } from "~components";
import { Css } from "~generated/css";
import { cleanValidationError, isValidEmail, isValidPhone, useTestIds } from "~utils";
import { useAddAuthorizedUser_SaveHomeownerMutation } from "./AddAuthorizedUserModal.generated";

export type AddAuthorizedUserModalProps = {
  onClose: () => void;
  showToast: (toastMessage: ToastMessageProps) => void;
};

type AddAuthorizedUserFormState = {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string | null;
};

export function AddAuthorizedUserModal(props: AddAuthorizedUserModalProps) {
  const { onClose, showToast } = props;
  const tid = useTestIds(props, "addAuthorizedUserModal");

  const [saveHomeowner, { loading: saving }] = useAddAuthorizedUser_SaveHomeownerMutation({
    refetchQueries: ["InternalConfigurationsLandingPage_AuthorizedUsers"],
  });

  // TODO: we can simplify this with formState and mobx but it'd really only save us having to manually validate the form
  // atm.
  const [form, setForm] = useState<AddAuthorizedUserFormState>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  function isFormValid() {
    const hasFirstName = form.firstName.length > 0;
    const hasLastName = form.lastName.length > 0;
    const hasValidEmail = isValidEmail(form.email);
    const hasValidPhone = isValidPhone(form.phone);

    return hasFirstName && hasLastName && hasValidEmail && hasValidPhone;
  }

  async function onSubmit() {
    await saveHomeowner({
      variables: {
        input: {
          ...form,
          canViewCheckout: true,
          redirectToCheckout: true,
        },
      },
      onCompleted: () => {
        showToast({
          variant: "success",
          title: "User Added",
          body: `User ${form.firstName} ${form.lastName} has been added as a user.`,
        });
        onClose();
      },
      onError: (error) => {
        const cleanErrorMessage = cleanValidationError(error.message);
        showToast({
          variant: "error",
          title: "Save Error",
          body: `We're having trouble saving this homeowner: ${cleanErrorMessage}`,
        });
      },
    });
  }

  return (
    <Modal
      size={{ width: "max-content", height: "max-content", maxWidth: "489px" }}
      title={
        <div css={Css.df.jcfs.gap2.$}>
          <span css={Css.t24.$}>Add User</span>
        </div>
      }
      description={"Adding a user will authorize an external homebuyer access to Checkout via email or Google login."}
      actionBtns={[
        { variant: "link", children: "CANCEL", xss: Css.$, onClick: onClose },
        {
          children: "SAVE",
          rounded: true,
          xss: Css.ttu.wPx(154).hPx(35).pxPx(56).pyPx(9).borderRadius("17.5px").$,
          onClick: onSubmit,
          disabled: !isFormValid() || saving,
          isLoading: saving,
        },
      ]}
      isOpen
      close={onClose}
      {...tid}
    >
      <div css={Css.px3.gap3.df.fdc.$}>
        {/* Names */}
        <div css={Css.df.gap3.$}>
          <div css={Css.mt3.w100.$}>
            <TextField
              required
              value={form.firstName || undefined}
              onChange={(e, firstName) => setForm((prev) => ({ ...prev, firstName }))}
              placeholder="First Name"
              label={NewFieldLabel("User First Name*")}
              cssOverrides={NewFieldStyleOverrides}
              {...tid.firstName}
            />
          </div>
          <div css={Css.mt3.w100.$}>
            <TextField
              required
              value={form.lastName || undefined}
              onChange={(e, lastName) => setForm((prev) => ({ ...prev, lastName }))}
              placeholder="Last Name"
              label={NewFieldLabel("User Last Name*")}
              cssOverrides={NewFieldStyleOverrides}
              {...tid.lastName}
            />
          </div>
        </div>
        {/* Email */}
        <div css={Css.mt3.$}>
          <TextField
            value={form.email || undefined}
            onChange={(e, email) => setForm((prev) => ({ ...prev, email }))}
            placeholder="Email"
            label={NewFieldLabel("User Email*")}
            cssOverrides={NewFieldStyleOverrides}
            {...tid.emailAddress}
          />
        </div>
        {/* Phone Number */}
        <div css={Css.mt3.$}>
          <PhoneField
            useConfigurationStyle
            value={form.phone || undefined}
            onChange={(phone) => setForm((prev) => ({ ...prev, phone }))}
            placeholder="(XXX) XXX-XXXX"
            {...tid.phone}
          />
        </div>
      </div>
    </Modal>
  );
}
